import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { ReactComponent as ArrowDownSvg } from 'src/assets/svg/arrow-down.svg'
import { ReactComponent as ExternalLinkSvg } from 'src/assets/svg/external-link.svg'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { ChartComment } from 'src/pages/ContentAnalysisDetail/mockData/commentsChartMockData'
import styles from './commentItem.module.scss'

interface CommentItemProps {
  comment: ChartComment
  isSelected?: boolean
  // Absent, the row is not selectable and behaves exactly as it did before.
  onToggleSelect?: (id: string) => void
  // Renders "View Details" next to the chevron; absent, nothing changes.
  onViewDetails?: () => void
}

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#86949D',
  positive: '#8080FF',
}

const highlightText = (
  text: string,
  highlightedWords: string[],
  sentiment: 'positive' | 'neutral' | 'negative'
) => {
  if (highlightedWords.length === 0) {
    return <span>{text}</span>
  }

  const pattern = highlightedWords
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')
  const regex = new RegExp(`(${pattern})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) => {
    const isHighlighted = highlightedWords.some(
      (word) => word.toLowerCase() === part.toLowerCase()
    )
    if (isHighlighted) {
      return (
        <span
          key={index}
          style={{ color: SENTIMENT_COLORS[sentiment], fontWeight: 500 }}
        >
          {part}
        </span>
      )
    }
    return <span key={index}>{part}</span>
  })
}

export const CommentItem: FC<CommentItemProps> = ({
  comment,
  isSelected = false,
  onToggleSelect,
  onViewDetails,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el || isExpanded) return
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
  }, [comment.content, isExpanded])

  useEffect(() => {
    const el = contentRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (isExpanded) return
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [isExpanded])

  const platformIcon = SOCIAL_ICONS[comment.platform]
  const isSelectable = !!onToggleSelect
  const showChevron = isOverflowing || isExpanded

  return (
    <div
      className={classNames(styles.commentItem, {
        [styles.selectable]: isSelectable,
        [styles.selected]: isSelected,
      })}
    >
      <div className={styles.header}>
        <div className={styles.platformInfo}>
          {/* As in Social Listening (LiveNotificationItem): the checkbox shows
              on hover or once checked, and only the checkbox selects — reading
              or opening a comment never adds it to the selection. */}
          {isSelectable && (
            <input
              aria-label={`Select comment by ${comment.author}`}
              checked={isSelected}
              className={styles.checkbox}
              onChange={() => onToggleSelect(comment.id)}
              type="checkbox"
            />
          )}
          <span className={styles.platformIcon}>{platformIcon}</span>
          <span className={styles.authorName}>{comment.author}</span>
          {comment.timestamp && (
            <span className={styles.commentDate}>
              {moment(comment.timestamp).format('D MMM YYYY')}
            </span>
          )}
          <a
            href={comment.postUrl}
            onClick={(e) => e.stopPropagation()}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ExternalLinkSvg className={styles.externalLinkIcon} />
          </a>
        </div>
        <div className={styles.actions}>
          {onViewDetails && (
            <button
              className={styles.viewDetails}
              onClick={(event) => {
                event.stopPropagation()
                onViewDetails()
              }}
              type="button"
            >
              View Details
              <span className={styles.viewDetailsChevron}>›</span>
            </button>
          )}
          {/* Only shown when the comment actually overflows the 2-line clamp;
              short comments don't get a chevron. Expanding is not selecting:
              the chevron keeps the click to itself so reading a long comment
              does not add it to the selection. */}
          {showChevron ? (
            <button
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse comment' : 'Expand comment'}
              className={styles.expandButton}
              onClick={(event) => {
                event.stopPropagation()
                setIsExpanded((prev) => !prev)
              }}
              type="button"
            >
              <ArrowDownSvg
                className={classNames(styles.chevron, {
                  [styles.expanded]: isExpanded,
                })}
              />
            </button>
          ) : (
            onViewDetails && (
              <span aria-hidden="true" className={styles.chevronPlaceholder} />
            )
          )}
        </div>
      </div>
      <div
        className={classNames(styles.content, {
          [styles.clamped]: !isExpanded,
        })}
        ref={contentRef}
      >
        {highlightText(
          comment.content,
          comment.highlightedWords,
          comment.sentiment
        )}
      </div>
    </div>
  )
}
